<script lang="ts">
import { computed, defineComponent, inject, onActivated, onDeactivated, onMounted, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formValidierung from "@/businesslogic/formValidierung";
import { NavvItem } from "@/model/NavItem";
import Logger from "js-logger";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Infotext from "@/components/Infotext.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import berechnungUtils from "@/businesslogic/utils/berechnungUtils";
import storeUtils from "@/businesslogic/utils/storeUtils";
import gebaeudeUtils from "@/businesslogic/utils/gebaeudeUtils";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "ImmobilienDetailsFH2024",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    ValidierungsFehler,
    Steps,
    Infotext,
    Zeile,
    Zelle,
    TitelZelle,
    Ueberschrift,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  setup(props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const feldBlurFocus: any = inject("feldBlurFocus");
    const { ermittleVersicherungssumme } = berechnungUtils();
    const manuellGesetzt = ref(false);
    const mehrfamilienhaus = computed(() => {
      return piniaStore.state.gebaeudedaten.gebaeudetyp === "3";
    });
    const photovoltaikInfoText =
      "Eine umfangreiche Absicherung, wie z.B. der Ertragsausfall oder Diebstahl der Paneele, ist möglich, " +
      "sofern Ihre Photovoltaikanlage folgende Kriterien erfüllt:<br />" +
      "1) max. 10 Jahre alt,<br />" +
      "2) max. 30 kWp und<br />" +
      "3) Installation oder Abnahme durch Fachfirma<br />" +
      "4) Installation auf oder am versicherten Haupt- oder Nebengebäude<br />" +
      "<u>Wichtig:</u> Gegen Schäden durch Feuer, Sturm und Hagel ist die PV-Anlage generell mitversichert.";
    const fehler = ref({} as any);
    const buttonText = ref("Weiter zur Tarifauswahl");
    const submitDeaktiviert = ref(false);
    const pvDetailfragenAnzeigenUndPruefen = computed(() => {
      return (
        piniaStore.state.ferienhaus.versicherungsumfang !== "0003" &&
        piniaStore.state.gebaeudedaten.photovoltaikVorhanden
      );
    });
    const baujahrMin = computed(() => {
      return piniaStore.state.gebaeudedaten.baujahr;
    });
    const baujahrMax = new Date().getFullYear() + 1;
    const baujahrFehlerText = computed(() => {
      return "Bitte geben Sie das vierstellige Baujahr (" + baujahrMin.value + " - " + baujahrMax + ") ein.";
    });
    const istAktiv = ref(false);
    const { berechneGebaeudetyp } = gebaeudeUtils();
    const versicherungssummeInfoText =
      "Die Versicherungssumme soll dem Versicherungswert des gesamten Hausrats " +
      "entsprechen, also dem Betrag, der für die Neuanschaffung des gesamten " +
      "Hausrats aufgewendet werden müsste. Ist die Versicherungssumme zu " +
      "niedrig angesetzt, liegt eine Unterversicherung vor und der Schaden wird " +
      "nur anteilsmäßig ersetzt. Einen Abzug wegen Unterversicherung können Sie " +
      "vermeiden, wenn Sie pro qm Wohnfläche eine Versicherungssumme von " +
      "650 Euro vereinbaren." +
      (piniaStore.state.isMakler
        ? '<br/><br/> Reichen Sie bitte ab einer Versicherungssumme von 65.000 € zusätzlich die Sicherungsbeschreibung Hausrat ein. Das Formular finden Sie im Menü Downloads in der Rubrik Hausrat. <br /> Schicken Sie die Sicherungsbeschreibung bitte nach Beantragung unter Nennung der Antragsnummer an <span><a class="inherited" href="mailto:maklerservice@grundvers.de">maklerservice@grundvers.de</a></span>'
        : "");

    const { erzwingeGanzzahl } = formValidierung();
    const {
      istFerienhaus,
      istMehrfamilienhaus,
      istEinfamilienhaus,
      istFerienwohnung,
      istVGVundVHV,
      istNurVHV,
      istNurVGV,
      photovoltaikAlterVersicherbar,
      pvLeistungVersicherbar,
    } = storeUtils();

    const { eq, lt, sub } = dataUtils();

    const zeigeHinweisPvAnlageBaujahr = computed(() => {
      return (
        piniaStore.state.isMakler &&
        piniaStore.state.gebaeudedaten.photovoltaikBaujahr !== null &&
        !photovoltaikAlterVersicherbar(piniaStore.state)
      );
    });

    const zeigeHinweisPvAnlageLeistung = computed(() => {
      return (
        piniaStore.state.isMakler &&
        piniaStore.state.gebaeudedaten.photovoltaikLeistung !== null &&
        !pvLeistungVersicherbar(piniaStore.state)
      );
    });

    const showables = ref({
      toggleMaklerHinweis: false,
    });

    const isEfhZfh = computed(() => {
      return (
        piniaStore.state.gebaeudedaten.gebaeudesubtyp !== "mfh" ||
        (piniaStore.state.gebaeudedaten.gebaeudesubtyp === "mfh" &&
          eq(piniaStore.state.gebaeudedaten.anzahlWohnungen, 2))
      );
    });

    const maxHausratWohnflaeche = computed(() => {
      return istMehrfamilienhaus(piniaStore.state) ? sub(piniaStore.state.ferienhaus.vgv.wohnflaeche, 1) : "5000";
    });

    const hausratWohnflaecheFehlermeldung = computed(() => {
      return istMehrfamilienhaus(piniaStore.state)
        ? "Die Wohnfläche darf nicht größer sein, als die des Gebäudes."
        : "Bitte geben Sie eine Fläche zwischen 1 und 5000 m² ein.";
    });

    watch(
      () => piniaStore.state.gebaeudedaten.nebengebaeudeVorhanden,
      (neu) => {
        Logger.debug("nebengebäude neu ", neu);
        if (neu === true) {
          submitDeaktiviert.value = true;
          toggleMaklerHinweis();
        } else {
          submitDeaktiviert.value = false;
        }
      },
    );
    watch(
      () => piniaStore.state.ferienhaus.vhv.wohnflaeche,
      () => {
        if (manuellGesetzt.value === false) {
          if (ermittleVersicherungssumme(piniaStore.state.ferienhaus.vhv.wohnflaeche) >= 150000) {
            piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme = 150000;
          } else {
            piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme = ermittleVersicherungssumme(
              piniaStore.state.ferienhaus.vhv.wohnflaeche,
            );
          }
        }
      },
    );
    watch(
      () => [piniaStore.state.ferienhaus.vgv.wohnflaeche, piniaStore.state.gebaeudedaten.wohnflaecheKeller],
      () => {
        if (manuellGesetzt.value === false && !mehrfamilienhaus.value) {
          const wohnflaecheGesamt =
            (piniaStore.state.ferienhaus.vgv.wohnflaeche == null ? 0 : piniaStore.state.ferienhaus.vgv.wohnflaeche) +
            (piniaStore.state.gebaeudedaten.wohnflaecheKeller == null
              ? 0
              : piniaStore.state.gebaeudedaten.wohnflaecheKeller);
          const vs = ermittleVersicherungssumme(wohnflaecheGesamt);
          if (vs > 150000) {
            piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme = 150000;
          } else {
            piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme = vs;
          }
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.anzahlWohnungen,
      () => {
        piniaStore.state.gebaeudedaten.gebaeudetyp = berechneGebaeudetyp(
          piniaStore.state.gebaeudedaten.gebaeudesubtyp,
          piniaStore.state.gebaeudedaten.anzahlWohnungen,
        );
      },
    );
    const textAenderungMakler = () => {
      if (piniaStore.state.isMakler) {
        buttonText.value = "Weiter zum Vorschlag";
      }
    };

    const eingabenSindValide = async () => {
      const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();

      await validiereForm();

      fehler.value = getFormFehler();
      const formFehlerAnzahl = getFormFehlerAnzahl();
      Logger.debug("Fehler", fehler.value, formFehlerAnzahl);
      return formFehlerAnzahl === 0;
    };

    const nextPage = () => {
      eingabenSindValide().then((isValid) => {
        Logger.debug("next-page > eingabenSindValide:", isValid);
        if (isValid) {
          piniaStore.state.neuberechnen = true;
          emit("next-page", { pageIndex: 1 });
        }
      });
    };

    const toggleMaklerHinweis = () => {
      showables.value.toggleMaklerHinweis = !showables.value.toggleMaklerHinweis;
    };

    onMounted(() => {
      textAenderungMakler();
    });

    watch(
      () => [piniaStore.state.berechnungDurchgefuehrt],
      () => {
        if (piniaStore.state.berechnungDurchgefuehrt && istAktiv.value) {
          piniaStore.state.berechnungDurchgefuehrt = false;
          emit("next-page", { pageIndex: 1 });
        }
      },
    );

    onActivated(() => {
      istAktiv.value = true;
      piniaStore.state.berechnungDurchgefuehrt = false;
      if (istEinfamilienhaus(piniaStore.state) && piniaStore.state.gebaeudedaten.anzahlWohnungen == -1) {
        piniaStore.state.gebaeudedaten.anzahlWohnungen = 1;
      }
      if (istMehrfamilienhaus(piniaStore.state) && lt(piniaStore.state.gebaeudedaten.anzahlWohnungen, 2)) {
        piniaStore.state.gebaeudedaten.anzahlWohnungen = -1;
      }
    });

    onDeactivated(() => {
      istAktiv.value = false;
    });

    return {
      nextPage,
      feldBlurFocus,
      wechsleSichtbarkeit,
      istSichtbar,
      piniaStore,
      fehler,
      props,
      buttonText,
      photovoltaikInfoText,
      baujahrFehlerText,
      baujahrMin,
      baujahrMax,
      erzwingeGanzzahl,
      pvDetailfragenAnzeigenUndPruefen,
      zeigeHinweisPvAnlageBaujahr,
      zeigeHinweisPvAnlageLeistung,
      toggleMaklerHinweis,
      showables,
      submitDeaktiviert,
      manuellGesetzt,
      versicherungssummeInfoText,
      istFerienhaus,
      istNurVHV,
      istNurVGV,
      istVGVundVHV,
      istMehrfamilienhaus,
      istEinfamilienhaus,
      istFerienwohnung,
      isEfhZfh,
      maxHausratWohnflaeche,
      hausratWohnflaecheFehlermeldung,
    };
  },
});
</script>

<template>
  <steps v-bind:items="navItems" :active="1" @goto-page="$emit('goto-page', $event)" />

  <section>
    <ueberschrift v-if="!istNurVHV(piniaStore.state)">Gebäudedetails</ueberschrift>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle :zeige-info="true" info-box-id="dachVarianteInfo">
        <strong>Dachart- und ausbau</strong>
      </titel-zelle>
      <zelle class="iconRadioDachausbau">
        <input
          id="dachAusgebaut"
          class="no-outline"
          name="dachvariante"
          v-model="piniaStore.state.gebaeudedaten.dachvariante"
          type="radio"
          value="3"
          required
          autofocus
          tabindex="0"
          aria-required="true"
          aria-label="ausgebautes Dach"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
          data-cy="LoftConversion"
        />
        <label for="dachAusgebaut" class="radiolabelDachausbau"></label>
        <input
          id="dachNichtAusgebaut"
          name="dachvariante"
          class="no-outline"
          v-model="piniaStore.state.gebaeudedaten.dachvariante"
          type="radio"
          value="2"
          aria-label="kein ausgebautes Dach"
          data-cy="LoftConversionNO"
        />
        <label for="dachNichtAusgebaut" class="radiolabelDachausbau"></label>
        <input
          id="flachdach"
          name="dachvariante"
          class="no-outline"
          v-model="piniaStore.state.gebaeudedaten.dachvariante"
          type="radio"
          value="1"
          aria-label="Flachdach"
          data-cy="FlatRoof"
        />
        <label for="flachdach" class="radiolabelDachausbau"></label>

        <validierungs-fehler :fehler-text="fehler.dachvariante" :reduzierter-abstand="true" />
        <Infotext
          name="dachVarianteInfo"
          css-klassen="o-grid__cell&#45;&#45;no-space"
          text="Als Vollgeschoss gelten alle zu Wohnzwecken ausgebauten Etagen. Ausgenommen sind Geschosse mit Schrägen (Dachausbau) sowie Keller."
        >
        </Infotext>
      </zelle>
    </zeile>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle :zeige-info="true" info-box-id="anzahlVollgeschosseInfo">
        <strong>Anzahl Vollgeschosse</strong>
      </titel-zelle>
      <zelle :breite="4">
        <select
          id="anzahlVollgeschosse"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlVollgeschosse"
          class="c-form-control-select"
          data-cy="NumberOfFloors"
          aria-label="Anzahl der Vollgeschoss"
        >
          <option selected value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
          <option value="7">7</option>
        </select>
        <infotext
          name="anzahlVollgeschosseInfo"
          css-klassen="o-grid__cell--no-space"
          text="Als Vollgeschoss gelten alle zu Wohnzwecken ausgebauten Etagen.
            Ausgenommen sind Dachgeschosse mit Schrägen sowie Keller."
        >
        </infotext>
      </zelle>
    </zeile>

    <zeile v-if="istMehrfamilienhaus(piniaStore.state) && istFerienhaus(piniaStore.state)">
      <titel-zelle>
        <strong>Anzahl Wohneinheiten</strong>
      </titel-zelle>
      <zelle :breite="4">
        <select
          id="anzahlWohneinheiten"
          name="anzahlWohneinheiten"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlWohnungen"
          class="c-form-control-select"
          data-cy="wohneinheiten"
          required
          aria-required="true"
          aria-label="Anzahl der Wohneinheiten"
        >
          <option selected value="-1">Bitte wählen</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="4">4</option>
          <option value="5">5</option>
          <option value="6">6</option>
          <option value="7">7</option>
          <option value="8">8</option>
          <option value="9">9</option>
          <option value="10">10</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.anzahlWohneinheiten" :reduzierter-abstand="true" />
      </zelle>
    </zeile>

    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle :zeige-info="true" info-box-id="wohnflaecheVGVInfo">
        <strong>Wohnfläche</strong> in m<sup>2</sup>
      </titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="wohnflaecheVGV"
          name="wohnflaecheVGV"
          v-model.trim.number="piniaStore.state.ferienhaus.vgv.wohnflaeche"
          class="c-form-control__input"
          :min="piniaStore.state.isMakler ? 1 : 15"
          max="5000"
          required
          aria-required="true"
          aria-label="Wohnfläche in Quadratmetern"
          placeholder="m²"
          :data-fehlermeldung="
            'Bitte geben Sie eine Fläche zwischen ' + (piniaStore.state.isMakler ? 1 : 15) + ' und 5.000 m² ein.'
          "
          data-cy="AreaLiving"
        />
        <validierungs-fehler :fehler-text="fehler.wohnflaecheVGV" />
      </zelle>
      <zelle :breite="8" :offset="4">
        <infotext
          name="wohnflaecheVGVInfo"
          css-klassen="o-grid__cell--no-space"
          text='Die Wohnfläche ist die Grundfläche aller Räume des Gebäudes
          einschließlich Hobbyräume. Nicht zu berücksichtigen sind Treppen,
          Balkone, Loggien und Terrassen sowie Keller-, Speicher- und
          Bodenräume, die nicht zu Wohn- oder Hobbyzwecken genutzt werden.<br />
          Alternativ akzeptieren wir die Angabe der Gesamtfläche entsprechend
          <ul style="padding-left: 0.9em;">
          <li>der Wohnflächenverordnung (WoFlV),</li>
          <li>der Nutzfläche gemäß DIN 277,</li>
          <li>
            dem Miet- oder Kaufvertrag, sofern dieser den aktuellen
            Ausbauzustand wiedergibt.
          </li>
          </ul>'
        >
        </infotext>
      </zelle>
    </zeile>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle><strong>Keller</strong> vorhanden?</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="kellerVorhandenNein"
          name="kellerVorhanden"
          v-model="piniaStore.state.gebaeudedaten.kellerVorhanden"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, es ist kein Keller vorhanden"
          data-fehlermeldung="Bitte wählen Sie die Art der Unterkellerung aus."
          data-cy="BasementNo"
        />
        <label for="kellerVorhandenNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="kellerVorhandenJa"
          name="kellerVorhanden"
          v-model="piniaStore.state.gebaeudedaten.kellerVorhanden"
          :value="true"
          aria-label="Ja, es ist ein Keller vorhanden"
          data-cy="Basement"
        />
        <label for="kellerVorhandenJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.kellerVorhanden" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.kellerVorhanden === true && !istNurVHV(piniaStore.state)">
      <titel-zelle v-if="piniaStore.state.gebaeudedaten.gewerbeVorhanden === false">
        <strong>Kellerfläche</strong> bei Wohn- oder Hobbynutzung in m<sup>2</sup>
      </titel-zelle>
      <titel-zelle v-else><strong>Kellerfläche</strong> bei Wohn- und Hobbynutzung in m<sup>2</sup></titel-zelle>

      <zelle>
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="wohnflaecheKeller"
          name="wohnflaecheKeller"
          v-model.number="piniaStore.state.gebaeudedaten.wohnflaecheKeller"
          class="c-form-control__input"
          placeholder="m²"
          min="0"
          :max="piniaStore.state.ferienhaus.vgv.wohnflaeche ? piniaStore.state.ferienhaus.vgv.wohnflaeche : 999"
          :required="piniaStore.state.gebaeudedaten.kellerVorhanden === true"
          :aria-required="piniaStore.state.gebaeudedaten.kellerVorhanden === true"
          aria-label="Kellerfläche bei Wohn- und Hobbynutzung in Quadratmetern"
          :data-fehlermeldung="
            'Bitte geben Sie eine Kellerfläche zwischen 0 und ' +
            (piniaStore.state.ferienhaus.vgv.wohnflaeche ? piniaStore.state.ferienhaus.vgv.wohnflaeche : 999) +
            ' m² ein.'
          "
          data-cy="AreaLivingBasement"
        />
        <validierungs-fehler :fehler-text="fehler.wohnflaecheKeller" />
      </zelle>
    </zeile>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle><strong>Garagen oder Carports</strong> vorhanden?</titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="parkplaetzeVorhandenNein"
          name="parkplaetzeVorhanden"
          :value="false"
          v-model="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          required
          aria-required="true"
          aria-label="Nein, es sind keine Parkplätze vorhanden"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="CarParkNo"
        />
        <label for="parkplaetzeVorhandenNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="parkplaetzeVorhandenJa"
          :value="true"
          name="parkplaetzeVorhanden"
          v-model="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          aria-label="Jan, es sind Parkplätze vorhanden"
          data-cy="CarPark"
        />
        <label for="parkplaetzeVorhandenJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.parkplaetzeVorhanden" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden === true && !istNurVHV(piniaStore.state)">
      <titel-zelle :zeige-info="true" info-box-id="garagenInfo">
        Anzahl der Stellplätze in <strong>Garagen</strong>
        {{ piniaStore.state.isMakler === true && isEfhZfh ? "außerhalb des Hauptgebäudes" : "" }}
      </titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlGaragen"
          name="anzahlGaragen"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlGaragen"
          class="c-form-control__input"
          placeholder="0-99"
          min="0"
          max="99"
          :required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          :aria-required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          aria-label="Anzahl der Stellplätze in Garagen"
          data-fehlermeldung="Bitte geben Sie die Anzahl der Stellplätze zwischen 0 und 99 ein."
          data-cy="GarageSpace"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlGaragen" />
        <infotext
          name="garagenInfo"
          css-klassen="o-grid__cell--no-space"
          text=" Zu Garagen zählen wir:<br />- freistehende Garagen<br />
            - angebaute Garagen<br />- Tiefgaragen"
        ></infotext>
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden === true && !istNurVHV(piniaStore.state)">
      <titel-zelle> Anzahl der Stellplätze in <strong>Carports</strong></titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="anzahlCarports"
          name="anzahlCarports"
          v-model.number="piniaStore.state.gebaeudedaten.anzahlCarports"
          class="c-form-control__input"
          placeholder="0-99"
          min="0"
          max="99"
          :required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          :aria-required="piniaStore.state.gebaeudedaten.parkplaetzeVorhanden"
          aria-label="Anzahl der Stellplätze in Carports"
          data-fehlermeldung="Bitte geben Sie die Anzahl der Stellplätze zwischen 0 und 99 ein."
          data-cy="CarportSpace"
        />
        <validierungs-fehler :fehler-text="fehler.anzahlCarports" />
      </zelle>
    </zeile>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle :zeige-info="true" info-box-id="photovoltaikInfo">
        <strong>Photovoltaikanlage</strong> auf dem Gebäude?
      </titel-zelle>
      <zelle class="mkRadio">
        <input
          type="radio"
          id="photovoltaikNein"
          name="photovoltaikVorhanden"
          v-model="piniaStore.state.gebaeudedaten.photovoltaikVorhanden"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, es befindet sich keine Photovoltaikanlage auf dem Gebäude"
          data-fehlermeldung="Bitte wählen Sie aus, ob eine PV-Anlage existiert."
          data-cy="PhotovoltaicNo"
        />
        <label for="photovoltaikNein" class="radiolabel">Nein</label>
        <input
          type="radio"
          id="photovoltaikJa"
          :value="true"
          name="photovoltaikVorhanden"
          v-model="piniaStore.state.gebaeudedaten.photovoltaikVorhanden"
          aria-label="Ja, es befindet sich eine Photovoltaikanlage auf dem Gebäude"
          data-cy="Photovoltaic"
        />
        <label for="photovoltaikJa" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.photovoltaikVorhanden" :reduzierter-abstand="true" />
        <infotext name="photovoltaikInfo" css-klassen="o-grid__cell--no-space" :text="photovoltaikInfoText"></infotext>
      </zelle>
    </zeile>
    <zeile v-if="pvDetailfragenAnzeigenUndPruefen">
      <titel-zelle><strong>Baujahr</strong> der Photovoltaikanlage</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="photovoltaikBaujahr"
          name="photovoltaikBaujahr"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.photovoltaikBaujahr"
          class="c-form-control__input"
          placeholder="JJJJ"
          :min="baujahrMin"
          :max="baujahrMax"
          :required="pvDetailfragenAnzeigenUndPruefen"
          :aria-required="pvDetailfragenAnzeigenUndPruefen"
          aria-label="Baujahr der Photovoltaikanlage"
          :data-fehlermeldung="baujahrFehlerText"
          data-cy="PhotovoltaicYear"
        />
        <div v-if="zeigeHinweisPvAnlageBaujahr">
          Der Einschluss einer zusätzlichen Absicherung der Photovoltaikanlage ist aufgrund der fehlenden
          Voraussetzungen des Alters der Anlage nicht möglich.<br />
          Hinweis: Gegen Schäden durch Feuer, Sturm und Hagel ist die PV-Anlage generell mitversichert.
        </div>
        <validierungs-fehler :fehler-text="fehler.photovoltaikBaujahr" />
      </zelle>
    </zeile>
    <zeile v-if="pvDetailfragenAnzeigenUndPruefen">
      <titel-zelle><strong>Leistung</strong> der Photovoltaikanlage in kWp (Spitzenleistung)</titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          id="photovoltaikLeistung"
          name="photovoltaikLeistung"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.photovoltaikLeistung"
          @keydown="erzwingeGanzzahl"
          class="c-form-control__input"
          placeholder="kWp"
          min="1"
          max="99"
          :required="pvDetailfragenAnzeigenUndPruefen"
          :aria-required="pvDetailfragenAnzeigenUndPruefen"
          aria-label="Leistung der Photovoltaikanlage in kWp (Spitzenleistung)"
          data-fehlermeldung="Bitte geben Sie die Leistung in ganzen kWp an (1-99)."
          data-cy="PhotovoltaicPower"
        />
        <div v-if="zeigeHinweisPvAnlageLeistung">
          Der Einschluss einer zusätzlichen Absicherung der Photovoltaikanlage ist aufgrund der fehlenden
          Voraussetzungen der Leistung der Anlage nicht möglich.<br />
          Hinweis: Gegen Schäden durch Feuer, Sturm und Hagel ist die PV-Anlage generell mitversichert.
        </div>
        <validierungs-fehler :fehler-text="fehler.photovoltaikLeistung" />
      </zelle>
    </zeile>
    <ueberschrift v-if="!istNurVHV(piniaStore.state)">Angaben zur Nutzung</ueberschrift>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle>Nutzungsart des Gebäudes?</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model.number="piniaStore.state.ferienhaus.vgv.nutzungsart"
          type="radio"
          id="eigennutzungVGV"
          name="nutzungsartVGV"
          :value="1"
          required
          aria-required="true"
          aria-label="Überwiegend Eigennutzung"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="eigennutzungVGV"
        />
        <label for="eigennutzungVGV" class="radiolabel">Überwiegend Eigennutzung</label>
        <input
          v-model.number="piniaStore.state.ferienhaus.vgv.nutzungsart"
          type="radio"
          id="fremdnutzungVGV"
          name="nutzungsartVGV"
          :value="2"
          aria-label="Überwiegend Fremdnutzung"
          data-cy="fremdnutzungVGV"
        />
        <label for="fremdnutzungVGV" class="radiolabel">Überwiegend Fremdnutzung</label>
        <validierungs-fehler :fehler-text="fehler.nutzungsartVGV" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="!istNurVHV(piniaStore.state)">
      <titel-zelle>
        <strong>Wieviele Tage im Jahr bewohnt/vermietet?</strong>
      </titel-zelle>
      <zelle :breite="4">
        <select
          id="unbewohnteTageVGV"
          name="unbewohnteTageVGV"
          v-model.number="piniaStore.state.ferienhaus.vgv.tageUnbewohnt"
          class="c-form-control-select"
          data-cy="unbewohnteTageVGV"
          required
          aria-required="true"
          aria-label="Wieviele Tage im Jahr bewohnt/vermietet?"
        >
          <option selected value="-1">Bitte wählen</option>
          <option value="3">Weniger als 95 Tage/Jahr</option>
          <option value="2">95-180 Tage/Jahr</option>
          <option value="1">Mehr als 180 Tage/Jahr</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.unbewohnteTageVGV" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <ueberschrift
      v-if="
        (!istNurVGV(piniaStore.state) && istMehrfamilienhaus(piniaStore.state)) ||
        (istVGVundVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state))
      "
      >Angaben zur Hausratversicherung
    </ueberschrift>
    <ueberschrift
      v-if="
        (istNurVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istFerienwohnung(piniaStore.state))
      "
      >Angaben zur Immobilie
    </ueberschrift>
    <zeile
      v-if="
        (!istNurVGV(piniaStore.state) && istMehrfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istFerienwohnung(piniaStore.state))
      "
    >
      <titel-zelle>Nutzungsart der zu versichernden Wohnung?</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.ferienhaus.vhv.nutzungsart"
          type="radio"
          id="eigennutzungVHV"
          name="nutzungsartVHV"
          :value="1"
          required
          aria-required="true"
          aria-label="Überwiegend Eigennutzung"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="eigennutzungVHV"
        />
        <label for="eigennutzungVHV" class="radiolabel">Überwiegend Eigennutzung</label>
        <input
          v-model="piniaStore.state.ferienhaus.vhv.nutzungsart"
          type="radio"
          id="fremdnutzungVHV"
          name="nutzungsartVHV"
          :value="2"
          aria-label="Überwiegend Fremdnutzung"
          data-cy="fremdnutzungVHV"
        />
        <label for="fremdnutzungVHV" class="radiolabel">Überwiegend Fremdnutzung</label>
        <validierungs-fehler :fehler-text="fehler.nutzungsartVHV" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile
      v-if="
        (!istNurVGV(piniaStore.state) && istMehrfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istFerienwohnung(piniaStore.state))
      "
    >
      <titel-zelle>
        <strong>Wieviele Tage im Jahr bewohnt/vermietet?</strong>
      </titel-zelle>
      <zelle :breite="4">
        <select
          id="unbewohnteTageVHV"
          name="unbewohnteTageVHV"
          v-model.number="piniaStore.state.ferienhaus.vhv.tageUnbewohnt"
          class="c-form-control-select"
          data-cy="unbewohnteTageVHV"
          required
          aria-required="true"
          aria-label="Wieviele Tage im Jahr bewohnt/vermietet?"
        >
          <option selected value="-1">Bitte wählen</option>
          <option value="3">Weniger als 95 Tage/Jahr</option>
          <option value="2">95-180 Tage/Jahr</option>
          <option value="1">Mehr als 180 Tage/Jahr</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.unbewohnteTageVHV" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile
      v-if="
        (!istNurVGV(piniaStore.state) && istMehrfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istEinfamilienhaus(piniaStore.state)) ||
        (istNurVHV(piniaStore.state) && istFerienwohnung(piniaStore.state))
      "
    >
      <titel-zelle><strong>Wohnfläche</strong> in m<sup>2</sup></titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="wohnflaecheVHV"
          name="wohnflaecheVHV"
          v-model.lazy.trim.number="piniaStore.state.ferienhaus.vhv.wohnflaeche"
          class="c-form-control__input"
          min="1"
          :data-fehlermeldung="hausratWohnflaecheFehlermeldung"
          required
          aria-required="true"
          aria-label="Wohnfläche in Quadratmetern"
          placeholder="m²"
          :max="maxHausratWohnflaeche"
          data-cy="AreaLiving2"
        />
        <validierungs-fehler :fehler-text="fehler.wohnflaecheVHV" />
      </zelle>
    </zeile>
    <zeile v-if="!istNurVGV(piniaStore.state)">
      <titel-zelle :zeige-info="true" info-box-id="versicherungssummeInfo">
        Höhe der Versicherungssumme in €
      </titel-zelle>
      <zelle :breite="4">
        <input
          type="number"
          @keydown="erzwingeGanzzahl"
          id="versicherungssumme"
          name="versicherungssumme"
          v-model.lazy.trim.number="piniaStore.state.ferienhaus.vhv.tarifinformationen.versicherungssumme"
          @blur="manuellGesetzt = true"
          class="c-form-control__input"
          min="1000"
          max="150000"
          required
          aria-required="true"
          aria-label="Höhe der Versicherungssumme in Euro"
          data-fehlermeldung="Bitte geben Sie eine Versicherungssumme in vollen Euro (zwischen 1000 und 150000 EUR) ohne Punkt und Komma ein."
          data-cy="versicherungssumme"
        />
        <validierungs-fehler :fehler-text="fehler.versicherungssumme" />
      </zelle>
      <zelle :breite="8" :offset="4">
        <infotext
          name="versicherungssummeInfo"
          css-klassen="o-grid__cell--no-space"
          :text="versicherungssummeInfoText"
          :schliesstBeiKlickAufText="false"
        />
      </zelle>
    </zeile>
    <template v-if="istNurVHV(piniaStore.state) && istFerienwohnung(piniaStore.state)">
      <ueberschrift>Geburtsdatum</ueberschrift>
      <zeile>
        <titel-zelle>{{ !piniaStore.state.isMakler ? "Ihr " : "" }}Geburtsdatum</titel-zelle>
        <zelle :breite="6">
          <input
            style="width: 50%"
            type="date"
            id="geburtsdatum"
            name="geburtsdatum"
            v-model.lazy.trim="piniaStore.state.personendaten.versicherungsnehmer.geburtsdatum"
            class="c-form-control__input"
            placeholder="TT.MM.JJJJ"
            title="Geburtsdatum im Format TT.MM.JJJJ"
            required
            aria-required="true"
            aria-label="Geburtsdatum"
            data-checkalter18bis100="true"
            data-cy="versicherungsnehmerGeburtstag"
          />
          <validierungs-fehler :fehler-text="fehler.geburtsdatum" />
        </zelle>
      </zeile>
    </template>
    <zeile>
      <zelle :breite="5" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          data-cy="submitZumVergleich"
        >
          Weiter zur Tarifauswahl
        </button>
      </zelle>
    </zeile>
  </section>
</template>
